import {
  DndContext,
  DragOverlay,
  MouseSensor,
  TouchSensor,
  useDraggable,
  useSensor,
  useSensors,
  type DragMoveEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { cn } from '../../lib/cn';

const GAP = 16;
const COLUMN_WIDTH = 240;
const LIST_WIDTH = 600;

interface Placement {
  x: number;
  y: number;
}

export interface GridLayout {
  columnWidth: number;
  positions: Map<string, Placement>;
  height: number;
}

/**
 * Disposition « masonry » de Keep : colonnes de 240 px, chaque carte va dans la colonne la plus courte.
 * Fonction pure, testée unitairement.
 */
export function computeLayout(
  ids: readonly string[],
  heights: ReadonlyMap<string, number>,
  containerWidth: number,
  mode: 'grid' | 'list',
): GridLayout {
  let columns =
    mode === 'list' ? 1 : Math.max(1, Math.floor((containerWidth + GAP) / (COLUMN_WIDTH + GAP)));
  // Sur mobile, Keep garde deux colonnes plus étroites.
  if (mode === 'grid' && columns < 2 && containerWidth >= 2 * 140 + GAP) columns = 2;
  const columnWidth =
    mode === 'list'
      ? Math.min(LIST_WIDTH, containerWidth)
      : Math.min(COLUMN_WIDTH, (containerWidth - (columns - 1) * GAP) / columns);
  const totalWidth = columns * columnWidth + (columns - 1) * GAP;
  const offsetX = Math.max(0, (containerWidth - totalWidth) / 2);

  const columnHeights = new Array<number>(columns).fill(0);
  const positions = new Map<string, Placement>();
  for (const id of ids) {
    let column = 0;
    for (let c = 1; c < columns; c++) if (columnHeights[c]! < columnHeights[column]!) column = c;
    positions.set(id, { x: offsetX + column * (columnWidth + GAP), y: columnHeights[column]! });
    columnHeights[column]! += (heights.get(id) ?? 0) + GAP;
  }
  return { columnWidth, positions, height: Math.max(0, Math.max(...columnHeights) - GAP) };
}

/** Trouve la carte (autre que celle déplacée) sous un point, en coordonnées de la grille. */
function hitTest(
  layout: GridLayout,
  heights: ReadonlyMap<string, number>,
  x: number,
  y: number,
  exclude: string,
) {
  for (const [id, pos] of layout.positions) {
    if (id === exclude) continue;
    const h = heights.get(id) ?? 0;
    if (x >= pos.x && x <= pos.x + layout.columnWidth && y >= pos.y && y <= pos.y + h) return id;
  }
  return null;
}

function useElementWidth<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  const [width, setWidth] = useState(0);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    setWidth(el.clientWidth);
    const observer = new ResizeObserver(() => setWidth(el.clientWidth));
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return [ref, width] as const;
}

interface ItemProps {
  id: string;
  x: number;
  y: number;
  width: number;
  animate: boolean;
  hidden: boolean;
  sortable: boolean;
  onHeight: (id: string, height: number) => void;
  children: ReactNode;
}

function GridItem({ id, x, y, width, animate, hidden, sortable, onHeight, children }: ItemProps) {
  const { setNodeRef, listeners, attributes } = useDraggable({ id, disabled: !sortable });
  const local = useRef<HTMLDivElement>(null);

  // Mesure synchrone avant affichage, puis suivi des changements de taille (image chargée, texte modifié…).
  useLayoutEffect(() => {
    const el = local.current;
    if (!el) return;
    onHeight(id, el.offsetHeight);
    const observer = new ResizeObserver(() => onHeight(id, el.offsetHeight));
    observer.observe(el);
    return () => observer.disconnect();
  }, [id, onHeight]);

  return (
    <div
      ref={(el) => {
        local.current = el;
        setNodeRef(el);
      }}
      {...(sortable ? listeners : {})}
      {...(sortable ? { 'aria-roledescription': attributes['aria-roledescription'] } : {})}
      style={{
        width,
        transform: `translate3d(${x}px, ${y}px, 0)`,
        transition: animate ? 'transform 200ms ease' : undefined,
        visibility: hidden ? 'hidden' : undefined,
      }}
      className="absolute top-0 left-0"
    >
      {children}
    </div>
  );
}

export interface MasonryGridProps<T extends { id: string }> {
  items: T[];
  mode: 'grid' | 'list';
  /** Autorise le glisser-déposer (désactivé pour la corbeille, les rappels, la recherche…). */
  sortable: boolean;
  renderItem: (item: T) => ReactNode;
  renderOverlay?: (item: T) => ReactNode;
  /** Appelé au dépôt avec les voisines finales de la note déplacée. */
  onMove?: (id: string, beforeId: string | null, afterId: string | null) => void;
  className?: string;
}

/**
 * Grille de notes : positionnement absolu calculé (pas de CSS columns, pour garder l'ordre
 * de lecture gauche → droite) et réorganisation en direct pendant le glisser-déposer.
 */
export function MasonryGrid<T extends { id: string }>({
  items,
  mode,
  sortable,
  renderItem,
  renderOverlay,
  onMove,
  className,
}: MasonryGridProps<T>) {
  const [containerRef, width] = useElementWidth<HTMLDivElement>();
  const [heights, setHeights] = useState<ReadonlyMap<string, number>>(new Map());
  const [dragOrder, setDragOrder] = useState<string[] | null>(null);
  const [activeId, setActiveId] = useState<string | null>(null);
  const pointerStart = useRef<{ x: number; y: number } | null>(null);
  const settling = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Une carte n'est animée qu'une fois déjà placée : pas d'envol depuis le coin au premier affichage.
  const placed = useRef(new Set<string>());

  const byId = useMemo(() => new Map(items.map((item) => [item.id, item])), [items]);
  const order = useMemo(
    () => (dragOrder ? dragOrder.filter((id) => byId.has(id)) : items.map((item) => item.id)),
    [dragOrder, items, byId],
  );
  const layout = useMemo(
    () => computeLayout(order, heights, width, mode),
    [order, heights, width, mode],
  );

  const onHeight = useCallback((id: string, height: number) => {
    setHeights((prev) => {
      if (prev.get(id) === height) return prev;
      const next = new Map(prev);
      next.set(id, height);
      return next;
    });
  }, []);

  useEffect(() => {
    for (const id of order) if (heights.has(id)) placed.current.add(id);
  });

  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 300, tolerance: 8 } }),
  );

  const onDragStart = ({ active, activatorEvent }: DragStartEvent) => {
    const event = activatorEvent as MouseEvent | TouchEvent;
    const point = 'touches' in event ? event.touches[0] : event;
    pointerStart.current = point ? { x: point.clientX, y: point.clientY } : null;
    setActiveId(String(active.id));
    setDragOrder(items.map((item) => item.id));
  };

  const onDragMove = ({ active, delta }: DragMoveEvent) => {
    const container = containerRef.current;
    if (!container || !pointerStart.current || !dragOrder) return;
    const rect = container.getBoundingClientRect();
    const x = pointerStart.current.x + delta.x - rect.left;
    const y = pointerStart.current.y + delta.y - rect.top;
    const target = hitTest(layout, heights, x, y, String(active.id));
    if (!target) return;
    setDragOrder((current) => {
      if (!current) return current;
      const from = current.indexOf(String(active.id));
      const to = current.indexOf(target);
      if (from < 0 || to < 0 || from === to) return current;
      const next = [...current];
      next.splice(from, 1);
      next.splice(to, 0, String(active.id));
      return next;
    });
  };

  const onDragEnd = () => {
    let moved = false;
    if (activeId && dragOrder) {
      const original = items.map((item) => item.id);
      const index = dragOrder.indexOf(activeId);
      if (index >= 0 && original.indexOf(activeId) !== index) {
        moved = true;
        onMove?.(activeId, dragOrder[index - 1] ?? null, dragOrder[index + 1] ?? null);
      }
    }
    setActiveId(null);
    pointerStart.current = null;
    // On garde l'ordre glissé jusqu'à ce que les données (mises à jour de façon optimiste) suivent,
    // sinon la carte reviendrait une image à sa place d'origine.
    if (moved) settling.current = setTimeout(() => setDragOrder(null), 1500);
    else setDragOrder(null);
  };

  useEffect(() => {
    if (!settling.current) return;
    clearTimeout(settling.current);
    settling.current = null;
    setDragOrder(null);
  }, [items]);

  const onDragCancel = () => {
    setActiveId(null);
    setDragOrder(null);
  };

  const activeItem = activeId ? byId.get(activeId) : undefined;

  return (
    <DndContext
      sensors={sensors}
      onDragStart={onDragStart}
      onDragMove={onDragMove}
      onDragEnd={onDragEnd}
      onDragCancel={onDragCancel}
    >
      <div
        ref={containerRef}
        className={cn('relative w-full', className)}
        style={{ height: layout.height }}
      >
        {width > 0 &&
          order.map((id) => {
            const item = byId.get(id)!;
            const pos = layout.positions.get(id)!;
            return (
              <GridItem
                key={id}
                id={id}
                x={pos.x}
                y={pos.y}
                width={layout.columnWidth}
                animate={placed.current.has(id)}
                hidden={id === activeId}
                sortable={sortable}
                onHeight={onHeight}
              >
                {renderItem(item)}
              </GridItem>
            );
          })}
      </div>
      <DragOverlay dropAnimation={null}>
        {activeItem && (
          <div style={{ width: layout.columnWidth }} className="rotate-1 cursor-grabbing">
            {(renderOverlay ?? renderItem)(activeItem)}
          </div>
        )}
      </DragOverlay>
    </DndContext>
  );
}
